import React from "react"
import PropTypes from "prop-types"
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts"
import { formatCurrency, formatNumber, formatPercent, cuadranteBCG, claseABC } from "../helpers"

// Niveles HHI con umbrales aproximados (escala 0-10000 según convención Herfindahl)
const HHI_NIVELES = {
  baja:     { color: "#10b981", label: "Baja",     desc: "Catálogo bien diversificado" },
  moderada: { color: "#3b82f6", label: "Moderada", desc: "Diversificación normal" },
  alta:     { color: "#f59e0b", label: "Alta",     desc: "Concentración en pocos productos" },
  muy_alta: { color: "#ef4444", label: "Muy alta", desc: "Cuasi monopolio del catálogo" },
}

const HHIGauge = ({ hhi, nivel }) => {
  const cfg = HHI_NIVELES[nivel] || { color: "#64748b", label: nivel || "—", desc: "" }
  // Para el visual: el back devuelve hhi en escala 0-10000 normalmente, pero a veces
  // (como con Hipermar) viene reescalado tipo 74. Detectamos y normalizamos a 0-100 visual.
  const valorMostrar = Number(hhi) || 0
  const visualPct = valorMostrar > 100 ? Math.min(100, valorMostrar / 100) : valorMostrar
  const data = [{ name: "HHI", value: visualPct, fill: cfg.color }]
  return (
    <div style={{ position: "relative", width: 180, height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%"
          startAngle={210} endAngle={-30} data={data}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar background={{ fill: "#f1f5f9" }} dataKey="value" cornerRadius={10} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", textAlign: "center",
      }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#0f172a", lineHeight: 1 }}>
          {formatNumber(valorMostrar, valorMostrar < 100 ? 2 : 0)}
        </div>
        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700, marginTop: 4 }}>HHI</div>
        <div style={{
          marginTop: 4, fontSize: 11, fontWeight: 700, color: cfg.color,
          padding: "2px 10px", borderRadius: 999, background: `${cfg.color}15`, display: "inline-block",
        }}>
          {cfg.label}
        </div>
      </div>
    </div>
  )
}

HHIGauge.propTypes = { hhi: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), nivel: PropTypes.string }

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 200px",
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "18px 20px", minWidth: 0,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)

StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const BCGCard = ({ keyId, count, totalProductos }) => {
  const cfg = cuadranteBCG(keyId)
  const pct = totalProductos > 0 ? (count / totalProductos) * 100 : 0
  return (
    <div style={{
      flex: "1 1 220px", minWidth: 0,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderLeft: `4px solid ${cfg.border}`,
      borderRadius: 12, padding: "14px 16px",
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: cfg.color, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
        <i className={`mdi mdi-${cfg.icon}`} style={{ fontSize: 16 }} />
        {cfg.label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: cfg.color }}>{formatNumber(count)}</span>
        <span style={{ fontSize: 13, color: cfg.color, opacity: 0.7 }}>
          {formatPercent(pct)} del catálogo
        </span>
      </div>
      <div style={{ fontSize: 12, color: cfg.color, marginTop: 6, opacity: 0.85 }}>
        {cfg.desc}
      </div>
    </div>
  )
}

BCGCard.propTypes = { keyId: PropTypes.string, count: PropTypes.number, totalProductos: PropTypes.number }

const NivelHHI = ({ nivel }) => {
  const cfg = {
    baja:     { color: "#065f46", bg: "#d1fae5", label: "Baja concentración" },
    moderada: { color: "#1e40af", bg: "#dbeafe", label: "Concentración moderada" },
    alta:     { color: "#92400e", bg: "#fef3c7", label: "Alta concentración" },
    muy_alta: { color: "#991b1b", bg: "#fee2e2", label: "Muy alta concentración" },
  }[nivel] || { color: "#475569", bg: "#f1f5f9", label: nivel || "—" }
  return (
    <span style={{
      display: "inline-block", padding: "4px 12px", borderRadius: 999,
      background: cfg.bg, color: cfg.color, fontSize: 13, fontWeight: 700,
    }}>
      {cfg.label}
    </span>
  )
}

NivelHHI.propTypes = { nivel: PropTypes.string }

const ResumenTab = ({ resumen }) => {
  if (!resumen) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-chart-box-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin datos de resumen.</div>
      </div>
    )
  }

  const concentracion = resumen.concentracion || {}
  const bcg = resumen.matriz_bcg || {}
  const totalArticulos = resumen.total_articulos ?? 0
  const totalBcg = (bcg.estrellas || 0) + (bcg.vacas_lecheras || 0) + (bcg.preguntas || 0) + (bcg.perros || 0)

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* KPIs principales + gauge HHI */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={{ flex: "2 1 480px", display: "flex", flexWrap: "wrap", gap: 12 }}>
          <StatCard
            icon="mdi-package-variant-closed"
            label="Artículos activos"
            value={formatNumber(totalArticulos)}
            accent="#6366f1"
            sub={resumen.total_lineas != null ? `${formatNumber(resumen.total_lineas)} líneas` : null}
          />
          <StatCard
            icon="mdi-cash-multiple"
            label="Monto total período"
            value={formatCurrency(resumen.monto_total_periodo)}
            accent="#10b981"
          />
          <StatCard
            icon="mdi-calendar-range"
            label="Días del período"
            value={formatNumber(resumen.dias_periodo)}
            accent="#06b6d4"
          />
        </div>
        <div style={{
          flex: "1 1 240px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
          padding: "10px 16px", display: "flex", alignItems: "center", gap: 12,
        }}>
          <HHIGauge hhi={concentracion.hhi} nivel={concentracion.nivel} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Índice de concentración
            </div>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 6, lineHeight: 1.5 }}>
              {(HHI_NIVELES[concentracion.nivel]?.desc) || concentracion.recomendacion || "—"}
            </div>
            {concentracion.top_5_aporte_pct != null && (
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                Top 5 → {formatPercent(concentracion.top_5_aporte_pct)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Matriz BCG */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-baseline mb-3 flex-wrap gap-2">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-chart-scatter-plot me-2 text-primary" />
            Matriz BCG — clasificación estratégica
          </h6>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {formatNumber(totalBcg)} productos clasificados
          </span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <BCGCard keyId="estrellas" count={bcg.estrellas || 0} totalProductos={totalArticulos} />
          <BCGCard keyId="vacas_lecheras" count={bcg.vacas_lecheras || 0} totalProductos={totalArticulos} />
          <BCGCard keyId="preguntas" count={bcg.preguntas || 0} totalProductos={totalArticulos} />
          <BCGCard keyId="perros" count={bcg.perros || 0} totalProductos={totalArticulos} />
        </div>
      </div>

      {/* Concentración Pareto */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
          <i className="mdi mdi-podium-gold me-2 text-warning" />
          Concentración del catálogo — análisis Pareto
        </h6>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
          {[
            { label: "Top 5", value: concentracion.top_5_aporte_pct, accent: "#10b981" },
            { label: "Top 10", value: concentracion.top_10_aporte_pct, accent: "#3b82f6" },
            { label: "Top 50", value: concentracion.top_50_aporte_pct, accent: "#8b5cf6" },
            { label: "Top 100", value: concentracion.top_100_aporte_pct, accent: "#f59e0b" },
          ].map((c) => (
            <div key={c.label} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
                Aporte {c.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.accent, marginTop: 4 }}>
                {formatPercent(c.value ?? 0)}
              </div>
              {c.value > 0 && (
                <div style={{ marginTop: 6, height: 6, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(100, c.value)}%`, height: "100%", background: c.accent }} />
                </div>
              )}
            </div>
          ))}
        </div>

        {concentracion.recomendacion && (
          <div style={{
            background: "#eff6ff", border: "1px solid #bfdbfe",
            color: "#1e40af", padding: "10px 14px", borderRadius: 8,
            fontSize: 13,
          }}>
            <i className="mdi mdi-lightbulb-on-outline me-1" />
            <strong>Recomendación:</strong> {concentracion.recomendacion}
          </div>
        )}
      </div>

      {/* Resumen ABC si viene */}
      {resumen.resumen_clases && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
            <i className="mdi mdi-format-list-numbered me-2 text-primary" />
            Distribución ABC
          </h6>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {["A", "B", "C"].map(c => {
              const info = resumen.resumen_clases[c]
              if (!info) return null
              const cfg = claseABC(c)
              return (
                <div key={c} style={{
                  background: cfg.bg, borderLeft: `4px solid ${cfg.chartColor}`,
                  borderRadius: 10, padding: "12px 14px",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color, textTransform: "uppercase" }}>
                    Clase {c} · {cfg.desc}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4 }}>
                    <span style={{ fontSize: 22, fontWeight: 700, color: cfg.color }}>
                      {formatNumber(info.count)} prods.
                    </span>
                    <span style={{ fontSize: 13, color: cfg.color, fontWeight: 700 }}>
                      {formatPercent(info.porcentaje_aporte)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: cfg.color, marginTop: 4, opacity: 0.85 }}>
                    {formatCurrency(info.importe_total)}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

ResumenTab.propTypes = { resumen: PropTypes.object }

export default ResumenTab
